import React from "react";
import { Plus } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";

import { Button } from "../ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousel";

import { cn } from "../../utils/lib";

import { selectCity, setNewCityCreationMode } from "../../redux/pricing/slice";

export function PricingCitiesSlider({ className, ...props }) {
  const dispatch = useDispatch();

  // Auth State
  const authId = useSelector(({ auth }) => auth.user._id);
  const authToken = useSelector(({ auth }) => auth.token);

  // Extranet City State
  const cities = useSelector(({ pricing }) => pricing.cities);
  const cityIdList = Object.keys(cities);

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const newCityCreationMode = useSelector(
    ({ pricing }) => pricing.newCityCreationMode
  );

  const { isFetching, isFetched, isError } = useSelector(
    ({ pricing }) => pricing.citiesFetchingStatus
  );

  // Fetching all Cities
  React.useEffect(() => {
    dispatch({
      type: "EXTRANET_GET_ALL_CITIES",
      payload: {
        token: authToken,
        affiliateId: authId,
      },
    });
  }, [authToken, authId]);

  const handleAddNewCityClick = React.useCallback(() => {
    dispatch(setNewCityCreationMode({ mode: true }));
    dispatch(selectCity({ cityId: "" }));
  }, []);

  const handleCityClick = React.useCallback((cityId) => {
    dispatch(setNewCityCreationMode({ mode: false }));
    dispatch(selectCity({ cityId }));
  }, []);

  return (
    <div
      className={cn(
        "max-md:sticky top-0 bg-black flex justify-start items-center gap-1",
        className
      )}
      {...props}
    >
      <Button
        variant="outline"
        disabled={newCityCreationMode || isFetching}
        onClick={handleAddNewCityClick}
        className={cn(
          "[@media(pointer:fine)]:hover:bg-[#1e5cfd] bg-[#181E29] text-white border-none hover:text-white [@media(pointer:fine)]:hover:text-white select-none transition-transform duration-300 font-medium h-9 [&_svg]:size-5 [&_svg]:2xl:size-6",
          newCityCreationMode &&
            !isFetching &&
            "bg-[#144EE3] hover:bg-[#144EE3] hover:text-white disabled:opacity-100 text-white scale-95 disabled:cursor-not-allowed disabled:pointer-events-auto hover:[@media(pointer:fine)]:bg-[#1e5cfd] hover:[@media(hover:fine)]:text-white ",
          isFetching && "bg-[#181E29] hover:bg-[#181E29]"
        )}
      >
        <span className="text-sm hidden sm:inline-block">Add New City</span>
        <Plus />
      </Button>

      <div
        className="sm:hidden h-9 w-px border-dashed border-r mx-1"
        aria-hidden="true"
      />

      {isFetching && (
        <p className="text-white animate-pulse ml-2s">Fetching Cities...</p>
      )}

      {isFetched && cityIdList.length !== 0 && (
        <Carousel
          className="flex items-center overflow-hidden gap-x-2 mx-0 w-full"
          opts={{
            align: "center",
          }}
        >
          <CarouselPrevious className="max-sm:hidden border-transparent relative left-0 top-0 translate-y-0 p-4 bg-[#1e2633] text-white hover:bg-[#19202c] hover:text-white/30" />

          <CarouselContent className="px-0 w-full flex flex-nowrap flex-1 -ml-4">
            {cityIdList.map((cityId) => (
              <CarouselItem key={cityId} className="basis-auto w-fit pl-4">
                <Button
                  variant="outline"
                  disabled={currentSelectedCityId === cityId}
                  onClick={handleCityClick.bind(null, cityId)}
                  className={cn(
                    "[@media(pointer:fine)]:hover:bg-[#1e5cfd] bg-[#181E29] text-white border-none hover:text-white [@media(pointer:fine)]:hover:text-white select-none transition-transform duration-300 font-medium h-9",
                    !newCityCreationMode &&
                      currentSelectedCityId === cityId &&
                      "bg-[#144EE3] text-white scale-95 disabled:cursor-not-allowed disabled:pointer-events-auto disabled:opacity-100",
                    "text-sm xl:text-base capitalize"
                  )}
                >
                  {cities[cityId]?.cityName}, {cities[cityId]?.country}
                </Button>
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselNext className="bg-[#1e2633] text-white hover:bg-[#19202c] hover:text-white/30 max-sm:hidden border-transparent relative right-0 top-0 translate-y-0 p-4" />
        </Carousel>
      )}
    </div>
  );
}
